import { ApolloError } from '@apollo/client';
import { useGeneratedSalesPortalOnlineMenuQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-online-menu.generated';

export interface OnlineMenuItem {
  ingredients: string[];
  name: string;
}

interface OnlineMenuLoaderParams {
  restaurantUUID: string;
  skip?: boolean;
  supplierUUID: string;
}

interface OnlineMenuLoaderResult {
  error: ApolloError | undefined;
  loading: boolean;
  menuItems: OnlineMenuItem[];
  refetch: () => void;
}

/**
 * Loader hook for fetching the restaurant's online menu.
 *
 * @param params.restaurantUUID - The UUID of the restaurant whose menu to fetch
 * @param params.skip - Skips the fetch entirely
 * @param params.supplierUUID - The UUID of the supplier
 * @returns {@link OnlineMenuLoaderResult}: the menu items, a loading flag, an
 * error, and a refetch. The menu items are empty when the account has no
 * online menu.
 */
export const useOnlineMenuLoader = ({
  restaurantUUID,
  skip = false,
  supplierUUID,
}: OnlineMenuLoaderParams): OnlineMenuLoaderResult => {
  const { data, error, loading, refetch } =
    useGeneratedSalesPortalOnlineMenuQuery({
      skip,
      variables: {
        restaurantUUID,
        supplierUUID,
      },
    });

  const menuItems: OnlineMenuItem[] =
    data?.getBusinessDetails?.menu_items?.map((item) => ({
      ingredients: item.ingredients,
      name: item.item_name,
    })) ?? [];

  return { error, loading, menuItems, refetch: () => void refetch() };
};
